import { EApplicantType, EFinancingProgram, EProvince } from '@if/core/types'
import { FieldsetCard } from '@if/mui'
import { collectsExpensesAndIncome } from '@src/data/creditapp-selectors'
import { FinancingProgramConfigs } from '@src/types/Constants'
import { ApplicantDto } from '@src/types/CreditApplicationSchema'
import { useEffect } from 'react'
import { Control, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import AddressComponent from './components/AddressComponent'
import ApplicantExpensesSection from './components/ApplicantExpenseComponent'
import ApplicantIncomeSection from './components/ApplicantIncomeComponent'
import ApplicantInformationParameters from './components/ApplicantInformationComponent'

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  watch: UseFormWatch<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>
  errors: FieldErrors<ApplicantDto> | undefined
  applicantType: EApplicantType
  editDisabled: boolean
  financingProgramId: EFinancingProgram
  hasPrequalifyBeenPressed: boolean
  isSinNeeded: boolean | null
}

/**
 * The applicant (or co-applicant) sections rendered as a stack of FieldsetCards —
 * Information, Current Address, Expenses and Income. Every field is namespaced with
 * `${applicantType}` so this drives both `applicant.*` and `coApplicant.*`.
 */
const ApplicantSections = ({
  control,
  watch,
  setValue,
  errors,
  applicantType,
  editDisabled,
  financingProgramId,
  hasPrequalifyBeenPressed,
  isSinNeeded,
}: Props) => {
  const { t } = useTranslation()

  useEffect(() => {
    setValue(`${applicantType}.financingProgramId`, financingProgramId)
  }, [financingProgramId, applicantType, setValue])

  const applicantProvince = watch(`${applicantType}.currentAddress.stateIso`) as EProvince
  const hasLegalName = (watch(`${applicantType}.hasLegalName`) as boolean) ?? false

  useEffect(() => {
    if (hasLegalName) setValue(`${applicantType}.lastName`, '', { shouldValidate: true })
  }, [hasLegalName, applicantType, setValue])

  const useExpenseIncomeSections = collectsExpensesAndIncome(financingProgramId, applicantProvince)
  const toolTip = editDisabled ? t('applicationHeader.tooltips.lockedForModification') : ''

  return (
    <>
      <FieldsetCard title={t('editCreditApplication.information.label')}>
        <ApplicantInformationParameters
          applicantType={applicantType}
          formControl={control}
          hasLegalName={hasLegalName}
          hasPrequalifyBeenPressed={hasPrequalifyBeenPressed}
          disableEdit={editDisabled}
          isSinNeeded={isSinNeeded}
        />
      </FieldsetCard>

      <FieldsetCard title={t('editCreditApplication.address.label')}>
        <AddressComponent
          formControl={control}
          name={`${applicantType}.currentAddress`}
          supportedProvinces={FinancingProgramConfigs[financingProgramId].supportedProvinces}
          disableEdit={editDisabled}
        />
      </FieldsetCard>

      {useExpenseIncomeSections && (
        <>
          <FieldsetCard title={t('editCreditApplication.expenses.label')}>
            <ApplicantExpensesSection
              control={control}
              watch={watch}
              setValue={setValue}
              errors={errors?.expenses}
              applicantType={applicantType}
              showDetailedForm={false}
              disabled={editDisabled}
              toolTip={toolTip}
            />
          </FieldsetCard>

          <FieldsetCard title={t('editCreditApplication.income.label')}>
            <ApplicantIncomeSection
              formControl={control}
              applicantType={applicantType}
              disableEdit={editDisabled}
              toolTip={toolTip}
              financingProgramId={financingProgramId}
            />
          </FieldsetCard>
        </>
      )}
    </>
  )
}

export default ApplicantSections
